// Activiteiten: opschonen, valideren, categoriseren en Markdown-import.
// Port van legacy/app.js (sanitizeActivity, validateJSONSchema, parseMarkdownTable, ...).
import type { Activity, ActivityFilters, Energy, MainCategory, PersonsFilter, ValidationResult } from './types';

export const MAX_IMPORT_ITEMS = 500;
/** Bovengrens na samenvoegen (legacy MAX_TOTAL_ACTIVITIES). */
export const MAX_TOTAL_ACTIVITIES = 1000;

export const ACTIVITY_FIELD_LIMITS = {
  naam: 120,
  omschrijving: 600,
  aantalPersonen: 40,
  categorie: 40,
  locatie: 64,
} as const;
export const ACTIVITY_DUUR_MIN = 1;
export const ACTIVITY_DUUR_MAX = 600;
export const ACTIVITY_DUUR_DEFAULT = 30;
const SCORE_MAX = 1_000_000;

const MAIN_CATEGORY_KEYS: readonly MainCategory[] = ['buiten-hoog', 'buiten-rustig', 'binnen-hoog', 'binnen-rustig'];

type RawRecord = Record<string, unknown>;

function isRecord(v: unknown): v is RawRecord {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Tekst opschonen: alleen string/number, controletekens weg, witruimte samengevoegd, begrensd. */
export function cleanText(value: unknown, maxLen: number): string {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value)
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen);
}

export function normalizeType(type: unknown): Activity['type'] {
  return String(type ?? '').toLowerCase().includes('buiten') ? 'Buiten' : 'Binnen';
}

export function normalizeWeather(weer: unknown): Activity['weer'] {
  return String(weer ?? '').toLowerCase().includes('droog') ? 'Droog' : 'Alles';
}

/** { lat, lng } als de waarde geldige coördinaten bevat ("lat, lng" of "lat; lng"), anders null. */
export function parseLatLng(value: unknown): { lat: number; lng: number } | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const m = String(value).trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*[,;]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

function parsePositiveIntId(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

export function normalizeEnergy(energie: unknown): Energy {
  if (typeof energie !== 'string' || !energie) return 'Rustig';
  const e = energie.toLowerCase().trim();
  if (e.includes('hoog') || e.includes('veel') || e.includes('actief')) return 'Veel energie';
  return 'Rustig';
}

/**
 * Hoofdcategorie. Een geldige (ook legacy '-chill') categorie wint; anders afgeleid uit type × energie.
 */
export function getMainCategory(type: unknown, energie: unknown, categorie?: unknown): MainCategory {
  const c = typeof categorie === 'string' ? categorie.toLowerCase().trim() : '';
  if (c === 'buiten-chill') return 'buiten-rustig';
  if (c === 'binnen-chill') return 'binnen-rustig';
  if ((MAIN_CATEGORY_KEYS as readonly string[]).includes(c)) return c as MainCategory;
  const isBuiten = typeof type === 'string' && type.toLowerCase().includes('buiten');
  const isHoog = normalizeEnergy(energie) === 'Veel energie';
  if (isBuiten) return isHoog ? 'buiten-hoog' : 'buiten-rustig';
  return isHoog ? 'binnen-hoog' : 'binnen-rustig';
}

/**
 * Ruwe sanitatie; id blijft `null` als het ontbreekt/ongeldig is (sanitizeActivityList kent er dan één toe).
 */
function sanitizeRaw(raw: unknown, index: number): Omit<Activity, 'id'> & { id: number | null } {
  const src: RawRecord = isRecord(raw) ? raw : {};

  const naam = cleanText(src.naam, ACTIVITY_FIELD_LIMITS.naam) || `Activiteit ${Number(index) + 1}`;
  const omschrijving = cleanText(
    src.omschrijving !== undefined ? src.omschrijving : src.beschrijving,
    ACTIVITY_FIELD_LIMITS.omschrijving,
  );
  const type = normalizeType(typeof src.type === 'string' ? src.type : '');
  const weer = normalizeWeather(typeof src.weer === 'string' ? src.weer : '');
  const energie = normalizeEnergy(src.energie);

  let duur = Math.round(Number(typeof src.duur === 'string' || typeof src.duur === 'number' ? src.duur : NaN));
  if (!Number.isFinite(duur)) duur = ACTIVITY_DUUR_DEFAULT;
  duur = Math.min(ACTIVITY_DUUR_MAX, Math.max(ACTIVITY_DUUR_MIN, duur));

  let score = Math.floor(Number(typeof src.score === 'string' || typeof src.score === 'number' ? src.score : NaN));
  if (!Number.isFinite(score) || score < 0) score = 0;
  score = Math.min(score, SCORE_MAX);

  const coords = parseLatLng(src.locatie);
  const locatie = coords ? `${coords.lat}, ${coords.lng}` : '';

  const personenRaw =
    src.aantalPersonen !== undefined ? src.aantalPersonen : src.aantal_personen !== undefined ? src.aantal_personen : src.personen;
  const aantalPersonen = cleanText(personenRaw, ACTIVITY_FIELD_LIMITS.aantalPersonen);

  const categorie = getMainCategory(type, energie, cleanText(src.categorie, ACTIVITY_FIELD_LIMITS.categorie));

  return { id: parsePositiveIntId(src.id), naam, omschrijving, type, weer, locatie, duur, energie, aantalPersonen, categorie, score };
}

/**
 * Maakt van willekeurige invoer een geldige Activity (velden getrimd/begrensd, duur 1–600, locatie alleen als geldige lat,lng).
 * Ontbreekt een geldig id, dan wordt `index + 1` gebruikt; gebruik sanitizeActivityList voor gegarandeerd unieke id's.
 */
export function sanitizeActivity(raw: unknown, index: number): Activity {
  const a = sanitizeRaw(raw, index);
  return { ...a, id: a.id ?? Math.max(1, Math.floor(Number(index) || 0) + 1) };
}

/**
 * Schoont een lijst op en garandeert unieke positieve gehele id's (her-nummert waar nodig).
 * Ontbrekende, ongeldige of dubbele id's krijgen opvolgende nummers na het hoogste id.
 * `reservedIds` (optioneel) zijn al in gebruik en worden ook vermeden.
 */
export function sanitizeActivityList(raw: unknown[], reservedIds: readonly number[] = []): Activity[] {
  const input = Array.isArray(raw) ? raw : [];
  const sanitized = input.map((item, i) => sanitizeRaw(item, i));
  const used = new Set(reservedIds.filter((id) => Number.isSafeInteger(id) && id > 0));
  let nextId = 1;
  for (const id of used) nextId = Math.max(nextId, id + 1);
  for (const a of sanitized) if (a.id !== null) nextId = Math.max(nextId, a.id + 1);
  return sanitized.map((a) => {
    let id = a.id;
    if (id === null || used.has(id)) id = nextId++;
    used.add(id);
    return { ...a, id };
  });
}

/** Valideert tegen public/gameon_activiteiten.schema.json; Nederlandse foutmeldingen. */
export function validateActivities(data: unknown, maxErrors = 50): ValidationResult {
  const errors: string[] = [];
  const done = (): ValidationResult => ({ ok: errors.length === 0, errors });
  const push = (msg: string) => {
    if (errors.length < maxErrors) errors.push(msg);
  };

  if (!Array.isArray(data)) {
    push('Het bestand moet een lijst (array) met activiteiten bevatten.');
    return done();
  }
  if (data.length === 0) {
    push('De lijst bevat geen activiteiten.');
    return done();
  }
  if (data.length > MAX_IMPORT_ITEMS) {
    push(`Te veel activiteiten (${data.length}); maximaal ${MAX_IMPORT_ITEMS} per import.`);
    return done();
  }

  const isInteger = (v: unknown) => typeof v === 'number' && Number.isInteger(v);
  const isNumber = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
  const has = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);
  const TYPE_ENUM = ['Binnen', 'Buiten', 'binnen', 'buiten'];
  const REQUIRED = ['naam', 'type', 'weer', 'duur', 'energie'];
  const STRING_FIELDS = ['omschrijving', 'weer', 'locatie', 'energie', 'aantalPersonen', 'categorie'];

  for (let i = 0; i < data.length && errors.length < maxErrors; i++) {
    const item: unknown = data[i];
    const nr = i + 1;
    if (!isRecord(item)) {
      push(`Activiteit ${nr} is geen geldig object.`);
      continue;
    }
    const label =
      typeof item.naam === 'string' && item.naam.trim()
        ? `Activiteit ${nr} ("${item.naam.trim().slice(0, 40)}")`
        : `Activiteit ${nr}`;

    for (const field of REQUIRED) {
      if (!has(item, field)) push(`${label}: het verplichte veld "${field}" ontbreekt.`);
    }
    if (has(item, 'id') && !(isInteger(item.id) || typeof item.id === 'string')) {
      push(`${label}: "id" moet een geheel getal of tekst zijn.`);
    }
    if (has(item, 'naam')) {
      if (typeof item.naam !== 'string') push(`${label}: "naam" moet tekst zijn.`);
      else if (item.naam.length < 1) push(`${label}: "naam" mag niet leeg zijn.`);
    }
    if (has(item, 'type') && !TYPE_ENUM.includes(item.type as string)) {
      push(`${label}: "type" moet "Binnen" of "Buiten" zijn.`);
    }
    if (has(item, 'duur') && !isNumber(item.duur)) {
      push(`${label}: "duur" moet een getal (minuten) zijn.`);
    }
    if (has(item, 'score') && !isNumber(item.score)) {
      push(`${label}: "score" moet een getal zijn.`);
    }
    for (const field of STRING_FIELDS) {
      if (has(item, field) && typeof item[field] !== 'string') push(`${label}: "${field}" moet tekst zijn.`);
    }
  }
  return done();
}

/** Opsomming van fouten voor een statusmelding (max 3 + "…en nog N andere fouten"). */
export function formatValidationErrors(errors: string[], max = 3): string {
  const shown = errors.slice(0, max).map((e) => `• ${e}`);
  const rest = errors.length - max;
  if (rest > 0) shown.push(`• …en nog ${rest} andere fout${rest === 1 ? '' : 'en'}`);
  return shown.join('\n');
}

const MARKDOWN_ACTIVITY_FIELDS = new Set(['naam', 'omschrijving', 'type', 'weer', 'locatie', 'duur', 'energie', 'aantalPersonen', 'categorie', 'id']);

function mapMarkdownHeader(h: string): string {
  if (h.includes('aantal') || h.includes('persoon') || h.includes('personen') || h.includes('spelers')) return 'aantalPersonen';
  if (h.includes('omschrijving') || h.includes('beschrijving') || h.includes('info')) return 'omschrijving';
  if (h.includes('naam') || h.includes('titel') || h.includes('activiteit')) return 'naam';
  if (h.includes('type')) return 'type';
  if (h.includes('weer')) return 'weer';
  if (h.includes('locatie') || h.includes('coords') || h.includes('coördinaten')) return 'locatie';
  if (h.includes('duur') || h.includes('tijd') || h.includes('minuten')) return 'duur';
  if (h.includes('energie')) return 'energie';
  return h;
}

/**
 * Parse een Markdown-tabel (zoals public/gameon_activiteiten.md) naar ruwe activiteiten.
 * Levert maximaal MAX_IMPORT_ITEMS + 1 rijen, zodat de aanroeper "te veel" kan melden.
 * Velden zijn nog niet volledig opgeschoond: haal het resultaat door sanitizeActivityList.
 */
export function parseMarkdownTable(md: string): Partial<Activity>[] {
  if (typeof md !== 'string') return [];
  const lines = md
    .replace(/^﻿/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.startsWith('|'));
  if (lines.length < 3) return [];

  const rawHeaders = lines[0].split('|').map((h) => h.trim().toLowerCase()).filter((h) => h);
  const headerMap = rawHeaders.map(mapMarkdownHeader);

  const activities: Partial<Activity>[] = [];
  for (let i = 2; i < lines.length && activities.length <= MAX_IMPORT_ITEMS; i++) {
    const cols = lines[i]
      .split('|')
      .map((c) => c.trim())
      .filter((_c, idx, arr) => idx > 0 && idx < arr.length - 1);
    if (cols.length !== rawHeaders.length) continue;
    const obj: Record<string, unknown> = { score: 0 };
    headerMap.forEach((h, idx) => {
      if (!MARKDOWN_ACTIVITY_FIELDS.has(h)) return;
      const val = cols[idx];
      obj[h] = h === 'duur' ? parseInt(val, 10) || ACTIVITY_DUUR_DEFAULT : val;
    });
    if (!obj.naam) continue;
    obj.categorie = getMainCategory(obj.type, obj.energie, obj.categorie);
    activities.push(obj as Partial<Activity>);
  }
  return activities;
}

/** Legacy matchesPersons: lege `aantalPersonen` of geen filter = altijd passend. */
export function matchesPersons(aantalPersonen: string, filter: PersonsFilter | null): boolean {
  if (!filter) return true;
  if (!aantalPersonen) return true;
  const parts = String(aantalPersonen).toLowerCase().trim().split(',').map((s) => s.trim());
  if (filter === '1') return parts.includes('1');
  if (filter === '2') return parts.includes('2');
  if (filter === '3+') return parts.includes('3+') || parts.includes('3');
  return true;
}

const nameKey = (a: { naam?: unknown }) => String(a?.naam ?? '').trim().toLowerCase();

/**
 * Samenvoegen: bestaande blijven, nieuwe (op naam, hoofdletterongevoelig) krijgen verse id's.
 * Nieuwe items starten met score 0; dubbele namen binnen `incoming` worden één keer opgenomen.
 * Controleer zelf MAX_TOTAL_ACTIVITIES op het resultaat (zie countNewActivities).
 */
export function mergeActivities(current: Activity[], incoming: Activity[]): Activity[] {
  const existing = sanitizeActivityList(current);
  const seen = new Set(existing.map(nameKey).filter(Boolean));
  const fresh: RawRecord[] = [];
  for (const act of incoming) {
    const key = nameKey(act);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    fresh.push({ ...act, id: null, score: 0 });
  }
  const freshWithIds = sanitizeActivityList(fresh, existing.map((a) => a.id));
  return [...existing, ...freshWithIds];
}

/** Aantal items uit `incoming` dat bij mergeActivities echt nieuw is (voor meldingen als "3 nieuw, 2 dubbel"). */
export function countNewActivities(current: Activity[], incoming: Activity[]): number {
  const seen = new Set(current.map(nameKey).filter(Boolean));
  let n = 0;
  for (const act of incoming) {
    const key = nameKey(act);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    n++;
  }
  return n;
}

/** Export-vorm (schema-compatibel) voor JSON-download. Volgorde van velden zoals legacy-export. */
export function toExportShape(list: Activity[]): unknown[] {
  return sanitizeActivityList(list).map((a) => ({
    id: a.id,
    score: a.score,
    naam: a.naam,
    omschrijving: a.omschrijving,
    type: a.type,
    weer: a.weer,
    locatie: a.locatie,
    duur: a.duur,
    energie: a.energie,
    aantalPersonen: a.aantalPersonen,
    categorie: a.categorie,
  }));
}

/** Compacte vorm voor deellinks: alleen schema-velden, lege velden en scores weggelaten (legacy buildSharePayload). */
export function toSharePayload(list: Activity[]): Record<string, unknown>[] {
  return sanitizeActivityList(list).map((a) => {
    const out: Record<string, unknown> = { naam: a.naam, type: a.type, weer: a.weer, duur: a.duur, energie: a.energie };
    if (a.omschrijving) out.omschrijving = a.omschrijving;
    if (a.locatie) out.locatie = a.locatie;
    if (a.aantalPersonen) out.aantalPersonen = a.aantalPersonen;
    return out;
  });
}

function matchesEnergy(actEnergie: unknown, filter: Energy): boolean {
  const act = String(actEnergie ?? '').toLowerCase();
  const f = filter.toLowerCase();
  if (f.includes('veel') || f.includes('hoog')) {
    return act.includes('veel') || act.includes('hoog') || act.includes('actief');
  }
  if (f.includes('rust') || f.includes('chill')) {
    return act.includes('rust') || act.includes('chill') || act.includes('normaal') || act.includes('laag');
  }
  return true;
}

/** Filtert op weer + gebruikersfilters. Bij slecht weer verdwijnen "Droog"-activiteiten, tenzij weatherOverruled. */
export function filterActivities(
  list: Activity[],
  filters: ActivityFilters,
  opts: { isBadWeather: boolean; weatherOverruled: boolean },
): Activity[] {
  return list.filter((activity) => {
    const actWeer = String(activity.weer || 'alles').toLowerCase();
    if (!opts.weatherOverruled && opts.isBadWeather && actWeer === 'droog') return false;
    if (filters.type && String(activity.type || '').toLowerCase() !== filters.type.toLowerCase()) return false;
    if (filters.energie && !matchesEnergy(activity.energie, filters.energie)) return false;
    if (filters.personen && !matchesPersons(activity.aantalPersonen, filters.personen)) return false;
    return true;
  });
}

/**
 * Kiest maximaal `count` suggesties: voorkeur voor activiteiten die niet net getoond zijn,
 * gewogen op score + toeval (zelfde gedrag als legacy getTop5Activities). `random` is injecteerbaar voor tests.
 *
 * Geef de id's van de vorige selectie mee als `recentlyShownIds` en bewaar zelf de id's van het
 * resultaat voor de volgende aanroep (bij ≤ count kandidaten komen ze allemaal, in originele volgorde, terug).
 */
export function pickSuggestions(
  candidates: Activity[],
  opts: { count?: number; recentlyShownIds?: ReadonlySet<number>; random?: () => number },
): Activity[] {
  const count = Math.max(0, Math.floor(opts.count ?? 5));
  const random = opts.random ?? Math.random;
  const recent = opts.recentlyShownIds;
  if (candidates.length <= count) return [...candidates];

  let pool = candidates;
  if (recent && recent.size > 0) {
    const unseen = candidates.filter((a) => !recent.has(a.id));
    pool = unseen.length >= count ? unseen : [...unseen, ...candidates.filter((a) => recent.has(a.id))];
  }

  return pool
    .map((activity) => ({ activity, sortScore: (Number(activity.score) || 0) * 0.5 + random() * 10 }))
    .sort((a, b) => b.sortScore - a.sortScore)
    .slice(0, count)
    .map((s) => s.activity);
}
